import { Button, Form, Input, Radio, Select, Space } from "antd";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import DatePicker from "../../../core/components/DatePicker";
import QuickDatePicker from "../../../core/components/data-grid/QuickDatePicker";
import { useDataGrid } from "../../../core/components/data-grid/DataGridContext";
import { LocalStorageService } from "../../../core/service/local-storage-service";
import { PERMISSIONS, useUser } from "../../../core/context/UserContext";
import HospitalSelectBox from "../../hospital/HospitalSelectBox";
import HospitalGroupSelectBox from "../../hospital-group/HospitalGroupSelectBox";
import ModalitySelectBox from "../../modality/ModalitySelectBox";
import InstitutionSelect from "../../user/InstitutionSelect";
import { parseListInput } from "../../../core/utils/parse-list-input";
import { BILLING_STATUS_LIST, billingStatusKey } from "../../../enums/ReportBillingStatusEnum";
import {
    BILLING_DATE_TYPE as DATE_TYPE,
    buildDefaultReportBillingFilter,
    fromFilterDate,
    toFilterDates,
} from "./report-billing-filter-defaults";
import "../../../core/style/grid-filter.css";

// Filtre sayfa değişimlerinde korunur (rapor ekranıyla aynı davranış). Varsayılan, grid üstündeki hızlı filtre
// çubuğunun "Sıfırla"sıyla ortak (bkz. report-billing-filter-defaults).
const getDefaultFilter = () => window.reportBillingFilter || buildDefaultReportBillingFilter();

// Yapıştırırken baş/son boşlukları kırp (rapor ekranıyla aynı davranış).
const handleTrimPaste = (e) => {
    const text = (e.clipboardData || window.clipboardData)?.getData('text');
    if (text == null) return;

    const trimmed = text.trim();
    if (trimmed === text) return;

    e.preventDefault();
    document.execCommand('insertText', false, trimmed);
}

export default function ReportBillingFilter() {
    const { t } = useTranslation();
    const { hasPermission } = useUser();
    const { onFilterPageSort, queryModel } = useDataGrid();

    const defaultFilter = useRef(getDefaultFilter());
    const formRef = useRef();

    const [dateType, setDateType] = useState(defaultFilter.current.dateType ?? DATE_TYPE.CONFIRM);
    const [dates, setDates] = useState({
        dateBegin: fromFilterDate(defaultFilter.current.dateType, defaultFilter.current.dateBegin),
        dateEnd: fromFilterDate(defaultFilter.current.dateType, defaultFilter.current.dateEnd),
    });
    const [searchText, setSearchText] = useState(defaultFilter.current.searchText || '');

    // Hızlı arama açıkken tarih aralığı sunucuda da uygulanmaz; alanlar bunu yansıtmak için kapanır.
    const searchActive = !!(searchText && searchText.length > 0);

    const toFilter = (form, currentDates, currentDateType) => {
        const filter = {
            ...form,
            dateType: currentDateType,
            ...toFilterDates(currentDateType, currentDates.dateBegin, currentDates.dateEnd),
            accessionNumberList: parseListInput(form.accessionNumberText),
        };

        delete filter.accessionNumberText;

        // Uygulanan filtrenin kendisi saklanır — hızlı filtre çubuğu da aynısını yazar; sayfaya dönünce
        // hangisinden uygulandıysa o filtreyle açılır.
        window.reportBillingFilter = filter;

        return filter;
    }

    const apply = (form) => {
        onFilterPageSort({ filter: toFilter(form, dates, dateType) });
    }

    const reset = () => {
        window.reportBillingFilter = null;
        window.location.reload();
    }

    // Formu UYGULANAN filtreyle eşitler: filtre yalnız bu panelden değil grid üstündeki hızlı filtre çubuğundan da
    // (arama dahil) uygulanıyor (bkz. ReportBillingQuickFilterBar). Eşitlenmezse paneldeki bir sonraki "Ara"
    // oradan yapılan seçimi sessizce geri alır.
    const syncFormFromFilter = (applied) => {
        if (!applied || !formRef.current) return;

        const { dateType: appliedDateType, dateBegin, dateEnd, accessionNumberList, ...fields } = applied;

        // Uygulanan filtrede OLMAYAN alan formda da kalmamalı (ör. çubuktan "Sıfırla"): önce hepsini boşalt.
        const cleared = Object.fromEntries(
            Object.keys(formRef.current.getFieldsValue(true)).map(k => [k, undefined])
        );

        formRef.current.setFieldsValue({
            ...cleared,
            ...fields,
            accessionNumberText: (accessionNumberList || []).join(', '),
        });

        setSearchText(applied.searchText || '');

        const type = appliedDateType ?? DATE_TYPE.CONFIRM;
        setDateType(type);
        setDates({
            dateBegin: fromFilterDate(type, dateBegin),
            dateEnd: fromFilterDate(type, dateEnd),
        });
    }

    useEffect(() => {
        onFilterPageSort({ filter: defaultFilter.current });
    }, []);

    // Filtre nesnesi yalnız bir filtre UYGULANDIĞINDA yenilenir (sayfa/sıralama/tazeleme aynı nesneyi korur);
    // her uygulamada formu eşitle — kaynağı bu panel ya da hızlı filtre çubuğu.
    const appliedFilter = queryModel?.filter;
    useEffect(() => {
        if (!appliedFilter || Object.keys(appliedFilter).length === 0) return;

        syncFormFromFilter(appliedFilter);
    }, [appliedFilter]);

    return (
        <Form
            ref={formRef}
            className={'grid-filter-form'}
            layout={'vertical'}
            onFinish={apply}
            initialValues={{
                billingStatusList: defaultFilter.current.billingStatusList || [],
                hospitalIdList: defaultFilter.current.hospitalIdList || [],
                studyTypeList: [],
                modalities: [],
            }}
        >
            <div style={{
                background: searchActive ? 'var(--accent-soft)' : 'var(--surface-2)',
                padding: '8px',
                borderRadius: 6,
                marginBottom: 8,
                border: searchActive ? '1px solid var(--accent)' : '1px solid var(--border)'
            }}>
                <div style={{
                    marginBottom: 4,
                    fontSize: '11px',
                    color: searchActive ? 'var(--accent-dark)' : 'var(--text-2)',
                    fontStyle: 'italic'
                }}>
                    {t('Search by patient name, ID, accession number or description - searches all dates')}
                </div>
                <Form.Item name='searchText' style={{ marginBottom: 0 }}>
                    <Input.Search
                        placeholder={t('Type anything: name, ID, number...')}
                        allowClear
                        enterButton={t('Search')}
                        size="small"
                        onPaste={handleTrimPaste}
                        onSearch={() => formRef.current.submit()}
                        onChange={(e) => setSearchText(e.target.value)}
                    />
                </Form.Item>
            </div>

            <Space style={{ marginBottom: 16 }}>
                <Button type={'primary'} size={'small'} htmlType="submit">{t('Search')}</Button>
                <Button size={'small'} onClick={reset}>{t('Reset')}</Button>
            </Space>

            <Form.Item
                label={(
                    <Radio.Group
                        size={'small'}
                        disabled={searchActive}
                        options={[
                            { label: t('Confirm Date Filter'), value: DATE_TYPE.CONFIRM },
                            { label: t('Report Date'), value: DATE_TYPE.REPORT },
                            { label: t('Study Date'), value: DATE_TYPE.STUDY },
                        ]}
                        onChange={(e) => setDateType(e.target.value)}
                        value={dateType}
                    />
                )}
                style={{ margin: 0, padding: 0 }}
            >
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <DatePicker
                        disabled={searchActive}
                        locale={LocalStorageService.getAntLocale()}
                        value={dates.dateBegin}
                        showToday={false}
                        onChange={(date) => setDates({ ...dates, dateBegin: date })}
                    />
                    <DatePicker
                        disabled={searchActive}
                        locale={LocalStorageService.getAntLocale()}
                        value={dates.dateEnd}
                        showToday={false}
                        onChange={(date) => setDates({ ...dates, dateEnd: date })}
                    />
                </div>
            </Form.Item>

            <Form.Item>
                <QuickDatePicker
                    disabled={searchActive}
                    onChange={(start, end) => setDates({ dateBegin: start, dateEnd: end })}
                />
            </Form.Item>

            <Form.Item label={t('Billing Status')} name="billingStatusList">
                <Select mode={'multiple'} allowClear>
                    {BILLING_STATUS_LIST.map(status => (
                        <Select.Option key={status} value={status}>{t(billingStatusKey(status))}</Select.Option>
                    ))}
                </Select>
            </Form.Item>

            {hasPermission(PERMISSIONS.HOSPITAL_GROUP_FILTER) && (
                <Form.Item label={t('Hospital Groups')} name="hospitalGroupIdList">
                    <HospitalGroupSelectBox />
                </Form.Item>
            )}

            <Form.Item label={t('Hospital Name')} name="hospitalIdList">
                <HospitalSelectBox />
            </Form.Item>

            <Form.Item label={t('Modality')} name="modalities">
                <ModalitySelectBox />
            </Form.Item>

            <Form.Item label={t('Accession No')} name="accessionNumber">
                <Input allowClear />
            </Form.Item>

            <Form.Item label={`${t('Accession No')} (${t('Rows')})`} name="accessionNumberText">
                <Input.TextArea
                    autoSize={{ minRows: 1, maxRows: 4 }}
                    placeholder={t('Separate values with comma, space or new line')}
                />
            </Form.Item>

            <Form.Item label={t('Patient Id')} name="patientId">
                <Input allowClear />
            </Form.Item>

            <Form.Item label={t('Patient Name')} name="patientName">
                <Input allowClear />
            </Form.Item>

            <Form.Item label={t('Study Type')} name="studyTypeList">
                <Select mode={'multiple'} allowClear>
                    <Select.Option value={1}>{t('Routine')}</Select.Option>
                    <Select.Option value={2}>{t('Inpatient')}</Select.Option>
                    <Select.Option value={3}>{t('Emergency')}</Select.Option>
                    <Select.Option value={4}>{t('COMPARATIVE_EXAMINATION')}</Select.Option>
                </Select>
            </Form.Item>

            <Form.Item label={t('Hbys Transfer Status')} name="isHbysTransfer">
                <Select allowClear>
                    <Select.Option value={null}>{t('All')}</Select.Option>
                    <Select.Option value={true}>{t('Transferred')}</Select.Option>
                    <Select.Option value={false}>{t('Not Transferred')}</Select.Option>
                </Select>
            </Form.Item>

            {hasPermission(PERMISSIONS.VIEW_STUDY_INSTITUTION) && (
                <InstitutionSelect
                    hide={true}
                    formItem={{ label: t('Institution'), name: 'institutions' }}
                />
            )}
        </Form>
    )
}
